import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  FormField,
  FormSelect,
  FormTextarea,
  Checkbox,
} from "@/components/ui";
import {
  InquiryFormSchema,
  EVENT_TYPES,
  EVENT_TYPE_LABELS,
  SOURCES,
  SOURCE_LABELS,
  EMPTY_INQUIRY_FORM_VALUES,
  type InquiryFormValues,
} from "@/api/types/inquiry";
import { useCreateInquiry } from "@/api/hooks/useInquiries";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { toApiInput } from "./inquiryForm";

interface InquiryFormDialogProps {
  open: boolean;
  onClose: () => void;
}

export function InquiryFormDialog({ open, onClose }: InquiryFormDialogProps) {
  const createInquiry = useCreateInquiry();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InquiryFormValues>({
    resolver: zodResolver(InquiryFormSchema),
    defaultValues: EMPTY_INQUIRY_FORM_VALUES,
  });

  useEffect(() => {
    if (open) reset(EMPTY_INQUIRY_FORM_VALUES);
  }, [open, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await createInquiry.mutateAsync(toApiInput(values));
      toastSuccess("Inquiry created");
      onClose();
    } catch (err) {
      toastError("Couldn't create inquiry", getErrorMessage(err));
    }
  });

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="New inquiry">
      <DialogContent size="lg">
        {/* noValidate: zod/RHF owns validation (see ContactFormDialog for why). */}
        <form onSubmit={onSubmit} noValidate>
          <DialogHeader onClose={onClose}>New inquiry</DialogHeader>
          <DialogBody className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label="Contact name"
                required
                error={errors.contact_name?.message}
                {...register("contact_name")}
              />
              <FormField
                label="Event date"
                type="date"
                error={errors.event_date?.message}
                {...register("event_date")}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormSelect
                label="Event type"
                error={errors.event_type?.message}
                {...register("event_type")}
              >
                <option value="">Not sure yet</option>
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {EVENT_TYPE_LABELS[t]}
                  </option>
                ))}
              </FormSelect>
              <FormField
                label="Guest count"
                type="number"
                min={0}
                error={errors.guest_count?.message}
                {...register("guest_count")}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label="Venue name"
                error={errors.venue_name?.message}
                {...register("venue_name")}
              />
              <FormSelect
                label="Source"
                error={errors.source?.message}
                {...register("source")}
              >
                <option value="">Unknown</option>
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_LABELS[s]}
                  </option>
                ))}
              </FormSelect>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end">
              <FormField
                label="Budget band"
                placeholder="e.g. $1,000-$1,500"
                error={errors.budget_band?.message}
                {...register("budget_band")}
              />
              <div className="pb-2.5">
                <Checkbox
                  label="NA guests expected"
                  {...register("na_guests_expected")}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
              <Checkbox label="Has consented to email" {...register("email_consent")} />
              <Checkbox label="Has consented to SMS" {...register("sms_consent")} />
            </div>

            <FormTextarea
              label="Message"
              error={errors.message?.message}
              {...register("message")}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={createInquiry.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createInquiry.isPending}>
              {createInquiry.isPending ? "Saving..." : "Create inquiry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
